import React, { useRef, useState } from 'react';

type FileUploadProps = {
  onChange?: (file: File) => void;
  loading?: boolean;
  label?: string | React.ReactNode;
  labelClassName?: string;
  accept?: string;
  maxSize?: number;
  allowedFileTypes?: string[];
  onValidationError?: (error: string) => void;
  showFileSize?: boolean;
  className?: string;
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onChange,
  loading = false,
  label,
  labelClassName = 'text-tiny text-secondary text-mainText',
  accept,
  maxSize,
  allowedFileTypes,
  onValidationError,
  showFileSize = false,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    setError(null);

    if (allowedFileTypes && allowedFileTypes.length > 0) {
      const fileType = file.type.toLowerCase();
      if (!allowedFileTypes.includes(fileType)) {
        const errorMessage = `Only ${allowedFileTypes
          .map(type => type.split('/')[1]?.toUpperCase())
          .filter(Boolean)
          .join('/')} files are allowed`;
        setError(errorMessage);
        onValidationError?.(errorMessage);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    if (maxSize && file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      const errorMessage = `File size must be less than ${maxSizeMB}MB`;
      setError(errorMessage);
      onValidationError?.(errorMessage);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);

    try {
      onChange?.(file);
    } catch (err) {
      const errorMessage = 'File upload failed. Please try again.';
      setError(errorMessage);
      onValidationError?.(errorMessage);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      console.error('File upload error:', err);
    }
  };

  return (
    <div
      className={`flex flex-col items-center gap-2 w-full ${className || ''}`}
    >
      {label && <p className={labelClassName}>{label}</p>}
      <label className="bg-buttonPrimary hover:bg-buttonPrimaryHover text-contrast font-bold text-small px-4 py-2 rounded-lg cursor-pointer transition">
        Select File
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={handleFileChange}
          disabled={loading}
        />
      </label>

      {selectedFile && (
        <p className="text-medium text-mainText">
          {selectedFile.name}
          {showFileSize && (
            <span className="text-secondary">
              {' '}
              ({(selectedFile.size / 1024).toFixed(2)} KB)
            </span>
          )}
        </p>
      )}

      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
};
